import React, { Component } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import InputField from '@comicrelief/storybook/src/components/InputField/InputField';
import MarketingPreferences from '@comicrelief/storybook/src/components/MarketingConsent/MarketingConsentCheckbox';
import marketingPreferenceData from '../../data/MarketingPreferenceData.json';
import SendFormHeightMessage from '../../utils/SendFormHeightMessage';
import NameFields from '../../data/NameFields';

const ENDPOINT_URL = process.env.REACT_APP_ENDPOINT_URL;

/**
 * MainForm Component class
 */
class MainForm extends Component {
  /**
   * MainForm constructor
   */
  constructor(props) {
    super(props);
    this.state = {
      showErrors: false,
      validation: {},
      formDataSuccess: null,
      formDataError: null,
      preferencesFilled: null,
      overallErrorMessage: null,
    };
  }

  componentDidMount() {
    SendFormHeightMessage();
  }

  componentDidUpdate() {
    SendFormHeightMessage();
  }

  /**
   * Update state with value and validity from child
   * @param name
   * @param validation
   */
  setValidity(name, validation) {
    if (typeof this.state.validation[name] === 'undefined' ||
      // general check to prevent infinite loop
      (typeof this.state.validation[name] !== 'undefined' &&
        (this.state.validation[name].value !== validation.value ||
          this.state.validation[name].message !== validation.message ||
          // marketing consent checkboxes specific input fields check
          (typeof this.state.validation[name].fieldValidation !== 'undefined' && validation.fieldValidation !== this.state.validation[name].fieldValidation)
        )
      )) {
      const validationObj = this.state.validation;
      validationObj[name] = validation;
      const isCheckbox = typeof validation.fieldValidation !== 'undefined';
      let isCheckboxValid = true;

      // First check we've got associated subfields (so a checkbox), and that a choice has actually been made
      if (isCheckbox && validation.value !== null) {
        // Step through the associated MP config to find a match to this checkbox to determine if the field IS required
        let thisCheckboxConfig = marketingPreferenceData.Questions.filter(obj => obj.id === name);
        // Simplify the returned array to just the content we want
        thisCheckboxConfig = thisCheckboxConfig[0].field;

        Object.entries(validation.fieldValidation).forEach(([thisFieldName, thisFieldContents]) => {
          // Check the current fieldname against the config to see if it's required
          let isRequired = thisCheckboxConfig.filter(obj => obj.required === true && obj.name === thisFieldName);
          // Again, simplifying things as per our needs
          isRequired = isRequired.length > 0;

          // Consider the whole checkbox option invalid if any required field is empty, or any field is invalid
          if ((isRequired && thisFieldContents.value === '') || thisFieldContents.valid === false) {
            isCheckboxValid = false;
          }
        });
      }

      // Override the existing validation nightmare now we've checked properly
      if (isCheckbox) validationObj[name].valid = isCheckboxValid;

      this.setState({
        ...this.state,
        validation: validationObj,
      });
    }
  }

  /**
   * create the campaign settings values for submission to endpoint
   * @return {{}}
   */
  getCampaignSettings() {
    const settings = {};
    const campaign = this.getUrlParameters('campaign');
    const transSource = this.getUrlParameters('transSource');
    const transType = this.getUrlParameters('transType');

    settings.campaign = campaign !== null ? campaign : 'CR';
    settings.transSource = transSource !== null ? transSource : `${settings.campaign}_MarketingPreferences`;
    settings.transType = transType !== null ? transType : 'prefs';
    settings.transSourceUrl = this.getCurrentUrl();
    settings.timestamp = this.getTimestamp();

    return settings;
  }

  /**
   * Gets the timestamp and format
   * @return {number}
   */
  getTimestamp() {
    return Math.round((new Date()).getTime() / 1000);
  }

  /**
   * Gets the current hostname and replaces 'localhost' to a default or use the
   * browser current url.
   * @return string
   */
  getCurrentUrl() {
    let url;
    if (window.location.hostname === 'localhost') {
      url = 'http://local.comicrelief.com';
    } else {
      url = window.location.protocol + '//' + window.location.host + '/';
    }
    return url;
  }

  /**
   * gets the value from the querystring parameter
   * @param parameterName string
   * @param result        string|null
   * @return {*}
   */
  getUrlParameters(parameterName, result = null) {
    let tmp = [];
    let response = result;
    window.location.search
      .substr(1)
      .split('&')
      .forEach((item) => {
        tmp = item.split('=');
        if (tmp[0] === parameterName) response = tmp[1];
      });
    return response;
  }

  /**
   * Scrolls to and focus on erroring field.
   */
  scrollToError() {
    // Crummy timeout fix to get around error class handling without having to rewrite everything
    setTimeout(() => {
      const currentState = this.state.validation;
      const errorField = document.querySelector('.form-error');

      // Only run if we've check the state AND active class state
      if ((!currentState.firstname.valid || !currentState.lastname.valid) && errorField) {
        errorField.scrollIntoView({ behavior: 'smooth' });

        // Focus on the input if it IS an input
        if (errorField.querySelector('input') !== null) {
          // As scrollIntoView doesn't have a callback, set a timeout before focusing, else the page jumps
          setTimeout(() => {
            errorField.querySelector('input').focus();
          }, 1000);
        }
      } else if (this.state.showErrors) {
        // Else, the checkboxes must be erroring, so scroll to the 'overallError'
        document.querySelector('.form__row-text--contact').scrollIntoView({ behavior: 'smooth' });
      } else if (this.state.formDataError) {
        // Else, there's been some issue with submitting valid data, so scroll to the 'data' error msg:
        document.querySelector('.form__field-error-wrapper--data').scrollIntoView({ behavior: 'smooth' });
      }
    }, 0);
  }

  /**
   * Creates and returns the data object to submit.
   * @return {*}
   */
  createFormData() {
    // mandatory settings to post to api endpoint
    const settings = this.getCampaignSettings();

    // data from the fields
    const fieldData = {};
    const fieldValidation = this.state.validation;
    Object.entries(fieldValidation).forEach(([key, value]) => {
      let fieldValue = value.value;
      // change value for marketing consent checkboxes to integer and add subfield data
      if (typeof value.fieldValidation !== 'undefined' && value.value !== null) {
        fieldValue = value.value === 'yes' ? 1 : 0;
        Object.entries(value.fieldValidation).forEach(([subField, subFieldValue]) => {
          fieldData[subField] = subFieldValue.value;
        });
      }
      fieldData[key] = fieldValue;
    });

    // return combined object of form data and settings
    return Object.assign(settings, fieldData);
  }

  /**
   * Posts form data if form is valid.
   * @param e
   */
  handleSubmit(e) {
    e.preventDefault();
    const isValidForm = this.isValidForm();
    // if form is valid post to endpoint
    if (isValidForm === true) {
      // create formDataObject
      const formData = this.createFormData();

      // Ensure this update doesn't call a scrollToError again
      this.setState({
        showErrors: false,
      });

      const headers = { 'Content-Type': 'application/json' };

      // post form data and settings to endpoint
      axios.post(ENDPOINT_URL, formData, headers)
        .then((response) => {
          this.setState({ formDataSuccess: response.data.message });

          this.props.history.push({ pathname: '/success' });

          /* Pass email address to iframe parent (Drupal etc) */
          if (this.state.validation.permissionEmail.fieldValidation.email.value) {
            window.parent.postMessage('{"email_address":"' + this.state.validation.permissionEmail.fieldValidation.email.value + '"}', '*');
          }
        })
        .catch((error) => {
          this.setState({
            formDataError: error,
          });

          this.scrollToError();
        });
    } else {
      this.setState({
        showErrors: true,
      });

      this.scrollToError();
    }
  }

  /**
   * Checks if the form is valid by checking if there are any invalid fields and
   * by checking if at least one checkbox is ticked
   * @return {boolean}
   */
  isValidForm() {
    let validity = true;
    let checkboxCount = 0;
    let checkboxNullCount = 0;
    let preferencesFilled = false;
    const formFields = this.state.validation;
    let overallErrorMessage = null;

    // Count the checkbox inputs and check if there's any invalid field
    Object.keys(formFields).forEach((key) => {
      // If this field has subfields (so is a checkbox):
      if (typeof formFields[key].fieldValidation !== 'undefined') {
        // Add to the tally
        checkboxCount += 1;

        // If no choice has been made for this checkbox field:
        if (formFields[key].value === null) {
          // Add to THAT tally
          checkboxNullCount += 1;
        } else if (formFields[key].valid) {
          // Else we DO have a checked checkbox and the associated fields are valid:
          preferencesFilled = true;
        }
      }

      if ((formFields[key].valid === false || formFields[key].valid === '')) {
        validity = false;
      }
    });

    // If all checkboxes have a null value, the form is invalid
    validity = checkboxNullCount !== checkboxCount && validity === true;

    // Switch the error message depending on the type of error
    if (!validity) {
      overallErrorMessage = (checkboxNullCount === checkboxCount) ? 'Please make a selection' : 'Please fix the errors below';
    }

    this.setState({
      preferencesFilled,
      overallErrorMessage,
    });

    return validity;
  }

  render() {
    const { formDataError } = this.state;
    return (
      <div>
        <form onSubmit={e => (this.handleSubmit(e))} noValidate>
          { formDataError &&
          <div className="form__field-error-container form__field-error-wrapper form__field-error-wrapper--data" aria-live="assertive" role="status">
            <span className="form-error">Whoops! There&apos;s been an error with your submission. Please refresh the page or try again later.</span>
          </div>
          }
          <div className="form__field--wrapper form__field--names-background">
            <p> Please enter your name so we can match any updates with our database.</p>
            <div>
              {
                NameFields.map((element, index) => (
                  <div key={element.id} className="form__field--wrapper">
                    <InputField
                      type={element.type}
                      id={element.name}
                      name={element.name}
                      required={element.required}
                      placeholder={element.placeholder}
                      label={element.label}
                      pattern={element.pattern}
                      isValid={(valid, name) => { this.setValidity(name, valid); }}
                      showErrorMessage={this.state.showErrors}
                      invalidErrorText={element.errorMessage}
                      onFocus={index}
                    />
                  </div>
                ))
              }
            </div>
          </div>
          <div className="form__row form__row-text form__row-text--contact">
            <h3>How should we contact you?</h3>
            <p>
              Update as little or as much as you like. We&rsquo;ll only change what you tell us to.
            </p>
            { this.state.overallErrorMessage &&
            <div className="form__field-error-container form__field-error-wrapper" aria-live="assertive" role="status">
              <span className="form-error">{ this.state.overallErrorMessage }</span>
            </div>
            }
          </div>
          { marketingPreferenceData.Questions.map(item =>
            (<MarketingPreferences
              key={item.id}
              getValidation={(name, validation) => { this.setValidity(name, validation[name]); }}
              showErrorMessages={this.state.showErrors}
              itemData={item}
            />)) }
          <button type="submit" className="btn btn--red">Update Preferences</button>
          <p className="privacy-copy">
            To learn more, see our <a href="https://www.comicrelief.com/privacy-policy" className="link link--dark-purple inline" target="parent" rel="nofollow">privacy policy</a>
          </p>
        </form>
      </div>
    );
  }
}

MainForm.defaultProps = {
  history: { push: { } },
};

MainForm.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
};

export default MainForm;
